<template>
    <div class="text-white w-full h-full pt-5 pb-[30px]">
        <!-- 页面标题 H1（仅对 SEO 和屏幕阅读器可见） -->
        <h1 class="sr-only">
            {{ t('aiVideo.pageTitle') }} - {{ t('aiVideo.pageSubtitle') }}
        </h1>

        <!-- banner top header -->
        <div class="text-white grid md:grid-cols-3 grid-cols-1 gap-5 lg:flex mb-10">
            <NuxtLink v-for="item in videoBannerHeaderList" :key="item.title"
                class="relative item h-[130px] flex justify-between rounded-xl items-center cursor-pointer bg-[length:100%_100%] bg-no-repeat bg-center gap-4 overflow-hidden p-4 transition-all duration-300 lg:p-5 lg:flex-1"
                :to="item.path" :style="{ backgroundImage: `url(${item.bg})` }"
                :aria-label="`${t('common.goto')} ${item.title}`">
                <div class="flex flex-col items-start justify-center gap-[10px] whitespace-nowrap">
                    <img :src="item.left_logo" :alt="`${item.title} 图标`" class="size-10" width="40" height="40"
                        loading="lazy" />
                    <span class="flex items-center justify-start gap-[10px]">
                        <span class="text-xl flex items-center font-medium lg:truncate">{{ item.title }}</span>
                        <img src="https://static.weryai.com/growth/image/9fe6c56c9c6fecd6ab3ee2af82de8c0f.webp" alt="跳转箭头"
                            aria-hidden="true" class="size-6 arrow transition-all duration-300" width="24"
                            height="24" />
                    </span>
                </div>

                <img :src="item.right_icon" :alt="`${item.title} 装饰图`"
                    class="w-[175px] absolute right-[-28px] top-[2px]" width="175" height="130" loading="lazy" />
            </NuxtLink>
        </div>
        <!-- models -->
        <div class="mt-10">
            <AIModels :model-list="videoModelCards" wrap-mode />
        </div>
        <!-- ai tool -->
        <div class="mt-10">
            <AITools :tools-list="aiVideosToolsList" :wrap-mode="false" />
        </div>

        <ClientOnly>
            <tool-all-category-inspiration
                :inspirationTypes="['IMG_TO_VIDEO', 'TEXT_TO_VIDEO']"></tool-all-category-inspiration>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { definePageMeta, useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import { useToolsHooks } from '~/utils/useHooks'
import AIModels from '~/components/website/AIModels.vue'
import AITools from '~/components/website/AITools.vue'

const {
    videoModelsEnums,
    videoBannerHeaderList,
    aiVideoToolsEnums,
} = useToolsHooks()

const { t } = useI18n()

// 设置多语言 SEO Meta 标签
useMultilingualSeo({ pageType: 'aiVideo' })

definePageMeta({
    layout: 'default',
})

// 结构化数据
const requestUrlHref = useRequestURL().href

useHead({
    script: [
        // BreadcrumbList 结构化数据
        {
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: '微鲤AI',
                            item: 'https://www.weryai.com.cn',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('aiVideo.pageTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
        // ItemList 结构化数据 - AI 视频模型列表
        {
            key: 'itemlist-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    name: t('aiVideo.pageTitle'),
                    description: t('seo.aiVideo.description'),
                    numberOfItems: videoModelsEnums.value?.length || 0,
                    itemListElement:
                        videoModelsEnums.value?.map(
                            (item: any, index: number) => ({
                                '@type': 'ListItem',
                                position: index + 1,
                                name: item.title,
                                description: item.desc,
                                url: `https://www.weryai.com${item.path}`,
                            })
                        ) || [],
                })
            ),
        },
    ],
})

const handleVideoClick = (item: { model_key?: string }) => {
    const defaultModel = item['model_key']
    // 如果有 model_key，跳转到 image-to-video 并带上模型参数
    // 否则跳转到 video-to-video
    if (defaultModel) {
        navigateTo(`/image-to-video?defaultModel=${defaultModel}`)
    } else {
        navigateTo('/video-to-video')
    }
}

const aiVideosToolsList = computed(() => aiVideoToolsEnums.value)

const videoModelCards = computed(() => {
    return videoModelsEnums.value
})

// 页面浏览埋点
onMounted(() => {
    analyticsEvent('page_view_home')
})
</script>

<style lang="less" scoped>
// Screen reader only - 视觉隐藏但对 SEO 和屏幕阅读器可见
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.item:hover .arrow {
    transform: translateX(10px);
}

.auto-width-slide {
    width: auto !important;
}

.slide-item {
    white-space: nowrap;
    padding: 10px 0;
    background: transparent;
    border-radius: 20px;

    &:hover {
        video {
            transform: scale(1.1);
        }
    }
}

.banner-card {
    width: 632px;
    height: 316px;
}

:deep(.swiper-button-prev) {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-image: url('../../assets/images/common/right-arrow.svg');
    background-size: 24px 24px;
    background-repeat: no-repeat;
    background-position: center;
    background-color: rgb(0 0 0 / 0.4);
    rotate: 180deg;

    .swiper-navigation-icon {
        display: none;
    }

    top: 40%;

    &:hover {
        transform: scale(1.1);
    }
}

:deep(.swiper-button-next) {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-image: url('../../assets/images/common/right-arrow.svg');
    background-size: 24px 24px;
    background-repeat: no-repeat;
    background-position: center;
    background-color: rgb(0 0 0 / 0.4);

    .swiper-navigation-icon {
        display: none;
    }

    top: 40%;

    &:hover {
        transform: scale(1.1);
    }
}
</style>
